import { useState, useEffect, useMemo } from "react";
import styles from "./StockEPSHistoryChart.module.scss";
import { classNames } from "@/lib/utils";
import UpgradePlan from "../upgrade-plan/UpgradePlan";
import dayjs from "dayjs";
import { getStockAnalystsEstimatesEPS } from "@/controllers/stock-analysts";
import { getChartDataNew } from "@/controllers/chart-data";
import { Stock } from "@/models/stock";
import TimeSeriesChart, { type TimeSeries } from "@/components/ortex-charts/TimeSeriesChart";

interface PropsTypes {
    stock: Stock;
    /** Hands the loaded EPS and closing-price series to the page, which reads changes from them. */
    onData?: (eps: [number, number][], price: [number, number][], shortened: boolean) => void;
}

/** Price on the right scale, the 12-month-forward EPS estimate on the left, volume in a pane below. */
const StockEPSHistoryChart = ({ stock, onData }: PropsTypes) => {
    const [eps, setEps] = useState<[number, number][]>([]);
    const [price, setPrice] = useState<[number, number, number, number, number, number?][]>([]);
    const [chartShortened, setChartShortened] = useState(false);
    const [chartObfuscated, setChartObfuscated] = useState(false);

    useEffect(() => {
        const today = dayjs();
        // A week past the chart's year, so a change over the full year has a base to measure from.
        const oneYearsAgo = today.subtract(1, "years").subtract(7, "days");
        Promise.all([getStockAnalystsEstimatesEPS(stock.stock_id, oneYearsAgo.format("YYYY-MM-DD"), today.format("YYYY-MM-DD")), getChartDataNew(stock.tradingitem_id, "ohlcv", oneYearsAgo.format("YYYY-MM-DD"))]).then(([estimates, bars]) => {
            setChartShortened(estimates.ts_shortened);
            setChartObfuscated(estimates.obfuscated);
            setEps(estimates.estimates_eps_12mfwd ?? []);
            setPrice(bars ?? []);
            onData?.(
                estimates.estimates_eps_12mfwd ?? [],
                (bars ?? []).map((row) => [row[0], row[4]]),
                !!estimates.ts_shortened
            );
        });
    }, [stock.stock_id, onData]);

    const series = useMemo<TimeSeries[]>(
        () => [
            // Amber against the teal price line. A stock with no consensus draws no EPS axis at all.
            ...(eps.length ? [{ id: "eps", title: "EPS Estimate (12m fwd)", type: "line" as const, data: eps, axis: "left" as const, color: "#E8B04A", precision: 2 }] : []),
            { id: "price", title: `${stock.ticker} Price in ${stock.currency}`, type: "line", data: price.map((row) => [row[0], row[4]] as [number, number]), axis: "right", color: "#31ABA6", precision: 2 },
            { id: "volume", title: "Volume", type: "column", data: price.map((row) => [row[0], row[row.length - 1] as number] as [number, number]), color: "dodgerblue", pane: "volume", format: "compact", precision: 2 },
        ],
        [eps, price, stock.ticker, stock.currency]
    );

    return (
        <div className={classNames(styles["chart-container"], chartObfuscated && !chartShortened && "obfuscated")}>
            {chartShortened && <UpgradePlan />}
            <TimeSeriesChart series={series} paneHeights={{ volume: 0.35 }} initialRange="1y" exportName={`${stock.ticker}-eps-history`} />
        </div>
    );
};

export default StockEPSHistoryChart;
